import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import {
  UserX, TrendingDown, Filter, Clock,
  Megaphone, FileText, MessageSquare, CheckSquare, CalendarCheck, BarChart,
  ArrowRight, CheckCircle2, GraduationCap,
  Target, Zap, BarChart3, Shield,
} from "lucide-react";

const problems = [
  { icon: TrendingDown, text: "High volume of low-quality leads" },
  { icon: Filter, text: "Poor qualification before consultation" },
  { icon: UserX, text: "Drop-off between inquiry and enrollment" },
  { icon: Clock, text: "Inefficient follow-up processes" },
];

const systemSteps = [
  { icon: Megaphone, label: "Ads / Traffic", desc: "Increase high-converting traffic through SEO, Content Strategy, Google & Meta Ads" },
  { icon: FileText, label: "Structured Intake", desc: "Smart forms that pre-qualify every inquiry automatically" },
  { icon: CheckSquare, label: "Lead Qualification", desc: "Automated scoring to prioritize high-intent leads" },
  { icon: MessageSquare, label: "Email + WhatsApp Nurture", desc: "Multi-channel follow-up sequences that convert" },
  { icon: CalendarCheck, label: "Booking Consultations", desc: "Seamless scheduling for qualified prospects" },
  { icon: BarChart, label: "Optimization", desc: "We analyze your daily signals and optimize campaigns in real time" },
];

const caseStudies = [
  {
    id: "insd",
    client: "INSD",
    desc: "International School of Design · 3 Locations",
    problem: "Needed to attract the right student profile across 3 locations with high drop-off after inquiry.",
    results: ["62% increase in qualified applications", "3x inquiry-to-enrollment rate", "40% lower cost per lead"],
  },
  {
    id: "maac",
    client: "MAAC",
    desc: "Maya Academy of Advanced Creativity · 2 Locations",
    problem: "High competition in student acquisition with inconsistent lead flow across 2 centers.",
    results: ["85% increase in lead volume", "2.5x campaign efficiency", "45% better conversion"],
  },
  {
    id: "nsdt",
    client: "NSDT",
    desc: "National School of Design & Technology · Corporate + 2 Franchise",
    problem: "Low-quality leads with poor conversion across corporate and franchise locations.",
    results: ["70% improvement in lead quality", "3x better conversion rates", "55% more qualified inquiries"],
  },
  {
    id: "frameboxx",
    client: "Frameboxx",
    desc: "Institute of Emerging Media",
    problem: "Difficulty standing out in a crowded creative education market with low brand recall.",
    results: ["90% increase in qualified inquiries", "2x improvement in ad ROAS", "60% lower drop-off rate"],
  },
];

const whyItWorks = [
  { icon: Target, text: "We focus on enrollments—not just inquiries" },
  { icon: Zap, text: "Instant response to every inquiry" },
  { icon: BarChart3, text: "Full funnel visibility from click to enrollment" },
  { icon: Shield, text: "Systems that scale across campuses and programs" },
];

const EducationLanding = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-16">
        {/* Hero */}
        <section className="py-24 px-4 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <GraduationCap className="w-4 h-4" /> For Education Institutes
            </motion.div>
            <motion.h1
              className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              We Help Education Institutes Generate and Convert{" "}
              <span className="text-primary">Qualified Applicants</span>
            </motion.h1>
            <motion.p
              className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              From inquiry to enrollment—we build systems that fill your seats with the right students.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Link to="/contact">
                <Button size="lg" className="text-base px-8 py-6 gap-2">
                  Get a Free Audit <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>

        {/* Problems */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Sound Familiar?
              </h2>
              <p className="text-muted-foreground text-lg">
                Education institutes face unique conversion challenges.
              </p>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-5">
              {problems.map((p, i) => (
                <motion.div
                  key={i}
                  className="flex items-start gap-4 p-6 rounded-xl bg-destructive/5 border border-destructive/10"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center shrink-0">
                    <p.icon className="w-5 h-5 text-destructive" />
                  </div>
                  <p className="text-foreground font-medium pt-1.5">{p.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* System - Vertical */}
        <section className="py-20 px-4 bg-secondary">
          <div className="max-w-3xl mx-auto">
            <motion.div
              className="text-center mb-14"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                See It In Action
              </h2>
              <p className="text-muted-foreground text-lg">
                Here's what the journey looks like for your next student.
              </p>
            </motion.div>

            <div className="space-y-4">
              {systemSteps.map((step, i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-5 p-5 rounded-xl bg-card border border-border"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="w-12 h-12 rounded-full border-2 border-primary/20 bg-primary/5 flex items-center justify-center shrink-0">
                    <step.icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground">{step.label}</h3>
                    <p className="text-sm text-muted-foreground">{step.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Real Results */}
        <section className="py-20 px-4">
          <div className="max-w-5xl mx-auto">
            <motion.h2
              className="text-3xl md:text-4xl font-bold text-foreground text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Real Results
            </motion.h2>
            <div className="grid md:grid-cols-2 gap-6">
              {caseStudies.map((cs, i) => (
                <Link to={`/case-studies#${cs.id}`} key={i}>
                  <motion.div
                    className="p-6 rounded-xl bg-card border border-border hover:border-primary/30 hover:shadow-lg transition-all cursor-pointer group h-full"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <GraduationCap className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                          {cs.client}
                        </h3>
                        <p className="text-xs text-muted-foreground">{cs.desc}</p>
                      </div>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4 mt-3">{cs.problem}</p>
                    <div className="space-y-2">
                      {cs.results.map((r, j) => (
                        <div key={j} className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                          <span className="text-sm font-medium text-foreground">{r}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 flex items-center gap-1 text-sm text-primary font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      View full case study <ArrowRight className="w-3.5 h-3.5" />
                    </div>
                  </motion.div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Why It Works */}
        <section className="py-20 px-4 bg-secondary">
          <div className="max-w-4xl mx-auto">
            <motion.h2
              className="text-3xl md:text-4xl font-bold text-foreground text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Why Our Approach Works
            </motion.h2>
            <div className="grid sm:grid-cols-2 gap-5">
              {whyItWorks.map((item, i) => (
                <motion.div
                  key={i}
                  className="flex items-center gap-4 p-5 rounded-xl bg-card border border-border"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <p className="text-foreground font-medium">{item.text}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-24 px-4 bg-primary text-primary-foreground">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Fill More Seats With Qualified Students?
            </h2>
            <p className="text-primary-foreground/80 text-lg mb-8">
              Get a free audit and see exactly where applicants are dropping off—and how to fix it.
            </p>
            <Link to="/contact">
              <Button size="lg" variant="secondary" className="text-base px-8 py-6 gap-2">
                Get a Free Audit <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default EducationLanding;
